import { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { History, Check, Loader, Clock, Pause, Car, Search, Download, ArrowRight } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { buildOrdersTopic, getOrdersData, mapPortalStatusToReact, readOrdersCustomerId } from '../lib/ordersBootstrap';

const pageTranslations = {
  en: {
    pageTitle: 'Orders',
    allOrders: 'All Orders',
    searchPlaceholder: 'Search order or vehicle...',
    allStatus: 'All Status',
    completed: 'Completed',
    cancelled: 'Cancelled',
  },
  de: {
    pageTitle: 'Aufträge',
    allOrders: 'Alle Aufträge',
    searchPlaceholder: 'Auftrag oder Fahrzeug suchen...',
    allStatus: 'Alle Status',
    completed: 'Abgeschlossen',
    cancelled: 'Storniert',
  }
};

export default function OrdersNew() {
  const { language } = useLanguage();
  const t = (key) => pageTranslations[language][key] || key;
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState(() => getOrdersData());
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || 'all');
  const customerId = readOrdersCustomerId();

  useEffect(() => {
    setSearchQuery(searchParams.get('search') || '');
    setStatusFilter(searchParams.get('status') || 'all');
  }, [searchParams]);

  useEffect(() => {
    let isActive = true;
    let timerId = null;
    let after = 0;
    let consecutiveFailures = 0;

    const applyStatusUpdate = (orderNumber, status, changedAt) => {
      if (!orderNumber || !status) return;
      setOrders((currentOrders) => currentOrders.map((order) => {
        if (order.id !== orderNumber) return order;
        return {
          ...order,
          status: mapPortalStatusToReact(status),
          updatedAt: changedAt || order.updatedAt || order.createdAt,
        };
      }));
    };

    const scheduleNextPoll = (delay = 3000) => {
      if (!isActive) return;
      timerId = window.setTimeout(runPoll, delay);
    };

    const runPoll = async () => {
      const topic = buildOrdersTopic(customerId);
      if (!topic) return;
      const url = `/mercure/poll?topic=${encodeURIComponent(topic)}&after=${after}`;
      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) {
          consecutiveFailures += 1;
          if ([401, 403, 404].includes(response.status) || consecutiveFailures >= 5) return;
          scheduleNextPoll(Math.min(15000, 3000 * (consecutiveFailures + 1)));
          return;
        }
        const payload = await response.json();
        const events = Array.isArray(payload?.events) ? payload.events : [];
        consecutiveFailures = 0;
        if (Number.isInteger(payload?.latestSequence)) {
          after = payload.latestSequence;
        }
        for (const event of events) {
          const content = event?.payload?.content;
          if (!content || content.kind !== 'order.status_changed') continue;
          applyStatusUpdate(content.orderNumber, content.status, content.changedAt);
        }
      } catch (_error) {
        consecutiveFailures += 1;
        if (consecutiveFailures >= 5) return;
      }
      scheduleNextPoll(consecutiveFailures > 0 ? Math.min(15000, 3000 * (consecutiveFailures + 1)) : 3000);
    };

    runPoll();
    return () => {
      isActive = false;
      if (timerId !== null) window.clearTimeout(timerId);
    };
  }, [customerId]);

  const filteredOrders = orders.filter(order => {
    const statusMatch = statusFilter === 'all' || order.status === statusFilter;
    const normalizedSearch = searchQuery.trim().toLowerCase();
    const searchableText = [order.id, order.vehicle.manufacturer, order.vehicle.model, order.vehicle.series, order.vehicle.engine, order.vehicle.power, order.vehicle.ecu, order.tuning.type, ...(order.tuning.options || [])]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    const searchMatch = normalizedSearch === '' || searchableText.includes(normalizedSearch);
    return statusMatch && searchMatch;
  });

  const stats = {
    total: orders.length,
    completed: orders.filter(o => o.status === 'completed').length,
    processing: orders.filter(o => o.status === 'processing').length,
    pending: orders.filter(o => o.status === 'pending').length,
    parked: orders.filter(o => o.status === 'parked').length,
  };

  const KPI = ({ label, value, icon: Icon, tone }) => (
    <div style={{ position: 'relative', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '18px 18px 16px', boxShadow: 'var(--shadow-xs)', overflow: 'hidden' }}>
      <span style={{ position: 'absolute', top: 0, left: 0, width: 3, height: '100%', background: 'var(--accent)' }} />
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', paddingRight: 40 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 800, fontSize: 38, lineHeight: 1, marginTop: 12, letterSpacing: 'var(--ls-tight)' }}>{value}</div>
      <span style={{ position: 'absolute', top: 16, right: 16, width: 34, height: 34, borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: tone === 'kg' ? 'var(--surface-3)' : tone + '-soft' }}>
        <Icon style={{ width: 17, height: 17, strokeWidth: 2.5 }} />
      </span>
    </div>
  );

  const formatDate = (dateStr) => {
    return new Intl.DateTimeFormat(language === 'de' ? 'de-DE' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateStr));
  };

  return (
    <DashboardLayout title={t('pageTitle')}>
      <div style={{ padding: '0 26px 26px 26px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* KPI Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
          <KPI label="Gesamt Aufträge" value={stats.total} icon={History} tone="kg" />
          <KPI label="Abgeschlossen" value={stats.completed} icon={Check} tone="success" />
          <KPI label="In Bearbeitung" value={stats.processing} icon={Loader} tone="info" />
          <KPI label="Warten auf Prüfung" value={stats.pending} icon={Clock} tone="warning" />
          <KPI label="Geparkt" value={stats.parked} icon={Pause} tone="kg" />
        </div>

        {/* Orders Table */}
        <section style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-xs)' }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', padding: '18px 22px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 'var(--ls-tight)', fontSize: 20, color: 'var(--text)' }}>
              <History style={{ width: 19, height: 19, color: 'var(--accent)', strokeWidth: 2.5 }} />
              <span>{t('allOrders')}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', color: 'var(--text-muted)', minWidth: 240 }}>
                <Search style={{ width: 15, height: 15, flex: '0 0 auto', strokeWidth: 2 }} />
                <input placeholder={t('searchPlaceholder')} value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setSearchParams(e.target.value ? { search: e.target.value } : {}); }} style={{ flex: 1, border: 0, background: 'transparent', fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--text)', outline: 'none' }} />
              </label>
              <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setSearchParams(e.target.value !== 'all' ? { status: e.target.value } : {}); }} style={{ minWidth: 150, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', color: 'var(--text)', fontFamily: 'var(--font-sans)', fontSize: 13, cursor: 'pointer' }}>
                <option value="all">{t('allStatus')}</option>
                <option value="completed">{t('completed')}</option>
                <option value="cancelled">{t('cancelled')}</option>
              </select>
            </div>
          </div>

          {/* Table */}
          {filteredOrders.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1080 }}>
                <thead>
                  <tr>
                    {['Auftrag #', 'Datum', 'Fahrzeug', 'Tuning', 'Status', 'Credits', 'Aktionen'].map((h, i) => (
                      <th key={h} style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-subtle)', textAlign: i >= 5 ? 'right' : 'left', padding: '14px 18px', borderBottom: '1px solid var(--border-strong)', whiteSpace: 'nowrap', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((o, i) => (
                    <tr key={o.id}>
                      <td style={{ padding: '16px 18px', borderBottom: i < filteredOrders.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--accent-text)', fontWeight: 500 }}>{o.id}</span>
                      </td>
                      <td style={{ padding: '16px 18px', borderBottom: i < filteredOrders.length - 1 ? '1px solid var(--border)' : 'none', fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{formatDate(o.createdAt)}</td>
                      <td style={{ padding: '16px 18px', borderBottom: i < filteredOrders.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                          <span style={{ width: 36, height: 36, borderRadius: 'var(--radius-sm)', background: 'var(--accent-soft)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                            <Car style={{ width: 18, height: 18, strokeWidth: 2 }} />
                          </span>
                          <div>
                            <div style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 700, textTransform: 'uppercase', fontSize: 15, lineHeight: 1.1 }}>{[o.vehicle.manufacturer, o.vehicle.model, o.vehicle.series].filter(Boolean).join(' ') || '-'}</div>
                            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)', marginTop: 4 }}>{[o.vehicle.engine, o.vehicle.power, o.vehicle.ecu].filter(Boolean).join(' · ') || '-'}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '16px 18px', borderBottom: i < filteredOrders.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{o.tuning.type || '-'}</span>
                          {Array.isArray(o.tuning.options) && o.tuning.options.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {o.tuning.options.map((t, idx) => (
                                <span key={idx} style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '4px 8px', borderRadius: 'var(--radius-sm)', background: 'var(--carbon-800)', color: '#fff' }}>{t}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '16px 18px', borderBottom: i < filteredOrders.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <span style={{ padding: '6px 12px', borderRadius: 'var(--radius-pill)', background: o.status === 'completed' ? 'var(--success-soft)' : 'var(--danger-soft)', color: o.status === 'completed' ? 'var(--success)' : 'var(--danger)', fontWeight: 600, fontSize: 13, display: 'inline-block' }}>
                          {o.status === 'completed' ? t('completed') : t('cancelled')}
                        </span>
                      </td>
                      <td style={{ padding: '16px 18px', borderBottom: i < filteredOrders.length - 1 ? '1px solid var(--border)' : 'none', textAlign: 'right' }}>
                        <div style={{ lineHeight: 1.1 }}>
                          <b style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 800, fontSize: 20 }}>{o.credits}</b>
                          <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-subtle)', marginTop: 3 }}>Credits</span>
                        </div>
                      </td>
                      <td style={{ padding: '16px 18px', borderBottom: i < filteredOrders.length - 1 ? '1px solid var(--border)' : 'none', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                          {o.status === 'completed' && (
                            <button style={{ width: 32, height: 32, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-muted)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Download style={{ width: 15, height: 15, strokeWidth: 2 }} />
                            </button>
                          )}
                          <button style={{ width: 32, height: 32, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-muted)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => window.location.href = `/orders/${o.id}`}>
                            <ArrowRight style={{ width: 15, height: 15, strokeWidth: 2 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: '32px 22px', textAlign: 'center', color: 'var(--text-muted)' }}>Keine Aufträge gefunden</div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}
